import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Paper,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDashboard } from '../../contexts/DashboardContext';
import FormFieldRenderer from '../FormFieldRenderer';
import { toDisplayString } from '../../utils/flowResultUtils';
import { useVoiceConversation } from '../../hooks/useVoiceConversation';
import DemoVoiceControls from '../demo/DemoVoiceControls';

/**
 * Compact chat bar for header placement when chatPosition is 'header'.
 * Single-line input with expandable progress and form fields.
 * @param {{ darkMode?: boolean, startInConversation?: boolean }} props
 */
export default function DashboardChatBar({ darkMode, startInConversation = false }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSFF = useMediaQuery(theme.breakpoints.down('md'));
  const [input, setInput] = useState('');
  const [formData, setFormData] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const {
    runDefaultFlow, setLastInput, setError, config, loading, activeFlowState, submitFormToFlow,
    structuredOutput,
  } = useDashboard();

  const citizenMode = Boolean(
    config?.citizenMode || config?.metadata?.interviewFlow?.citizenMode,
  );
  const hasDefaultFlow = Boolean(config?.defaultFlowId || config?.default_flow_id)
    && !config?.studioPreviewInterview;
  const appId = config?._id || config?.id;
  const settingsPath = config?.runtimeSource === 'v2app'
    ? `/studio/apps/${appId}`
    : `/dashboard-builder?edit=${appId}`;

  useEffect(() => {
    if (activeFlowState.waitingForForm && activeFlowState.formFields?.length > 0) {
      const initial = {};
      activeFlowState.formFields.forEach((f) => {
        const k = f.name || f.id;
        if (k && f.value != null) initial[k] = f.value;
      });
      setFormData((prev) => ({ ...initial, ...prev }));
    } else {
      setFormData({});
    }
  }, [activeFlowState.waitingForForm, activeFlowState.formFields]);

  const sendText = useCallback(async (raw) => {
    const q = String(raw || '').trim();
    if (!q) return;
    setLastInput(q);
    setError(null);
    await runDefaultFlow(q);
    setInput('');
  }, [runDefaultFlow, setLastInput, setError]);

  const handleSend = useCallback(async () => {
    await sendText(input);
  }, [input, sendText]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFormFieldChange = useCallback((fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleFormSubmit = useCallback(async () => {
    if (!submitFormToFlow) return;
    setFormSubmitting(true);
    try {
      await submitFormToFlow(formData);
      setFormDialogOpen(false);
    } finally {
      setFormSubmitting(false);
    }
  }, [submitFormToFlow, formData]);

  const voice = useVoiceConversation({
    onDictate: (text) => setInput((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}${text}`),
    onUtterance: (text) => { sendText(text); },
  });

  useEffect(() => {
    if (!startInConversation || !voice.hasSupport) return undefined;
    voice.setConversationMode(true);
    const t = setTimeout(() => {
      try { voice.startListening(); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
    // Only boot once when parent requests conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startInConversation boot
  }, [startInConversation]);

  useEffect(() => {
    if (!voice.conversationMode || loading) return;
    const txt = toDisplayString(
      structuredOutput?.ai_response
        ?? structuredOutput?.message
        ?? structuredOutput?.routing?.rationale
        ?? structuredOutput?.routing?.primaryTrackLabel
        ?? '',
      '',
    );
    if (txt) voice.speakReply(txt);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- speak when result settles
  }, [structuredOutput, loading, voice.conversationMode]);

  const hasProgress = activeFlowState.isActive || loading;
  const hasForm = activeFlowState.waitingForForm && activeFlowState.formFields?.length > 0;
  const showFormInDialog = isSFF && hasForm;

  useEffect(() => {
    if (hasForm && isSFF) setFormDialogOpen(true);
  }, [hasForm, isSFF]);

  const formContent = (
    <>
      <Typography variant="caption" fontWeight="medium" display="block" sx={{ mb: 1 }}>
        {activeFlowState.formTitle || 'Form input'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        {(activeFlowState.formFields || []).map((field, idx) => (
          <FormFieldRenderer
            key={field.name || field.id || idx}
            type={field.type || 'text'}
            label={field.label || field.name}
            value={formData[field.name || field.id] ?? ''}
            onChange={(e) => handleFormFieldChange(field.name || field.id, e?.target?.value ?? e)}
            required={field.required}
            options={field.options}
          />
        ))}
      </Box>
      <Button
        variant="contained"
        fullWidth={isSFF}
        onClick={handleFormSubmit}
        disabled={formSubmitting}
        startIcon={formSubmitting ? <CircularProgress size={16} /> : null}
        sx={isSFF ? { minHeight: 44 } : {}}
      >
        {formSubmitting ? 'Submitting…' : 'Submit'}
      </Button>
    </>
  );

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 1, sm: 2 }, py: 1 }}>
          <TextField
            size="small"
            placeholder={voice.conversationMode ? 'Listening — speak your question…' : 'Type or tap mic…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ flex: 1, minWidth: 80 }}
            disabled={!hasDefaultFlow}
            InputProps={{ sx: isSFF ? { minHeight: 44 } : {} }}
          />
          <DemoVoiceControls
            dense={!isSFF}
            label=""
            hasStt={voice.hasStt}
            hasTts={voice.hasTts}
            isListening={voice.isListening}
            isSpeaking={voice.isSpeaking}
            conversationMode={voice.conversationMode}
            disabled={!hasDefaultFlow || loading}
            onToggleListen={() => (voice.isListening ? voice.stopListening() : voice.startListening())}
            onToggleConversation={() => {
              const next = !voice.conversationMode;
              voice.setConversationMode(next);
              if (next) {
                try { voice.startListening(); } catch { /* ignore */ }
              } else {
                voice.stopAll();
              }
            }}
            onSpeak={() => {
              const txt = toDisplayString(
                structuredOutput?.ai_response
                  ?? structuredOutput?.message
                  ?? structuredOutput?.routing?.rationale
                  ?? '',
                '',
              );
              if (txt) voice.speakReply(txt, { force: true });
            }}
            onStopSpeak={voice.cancelSpeech}
          />
          <IconButton color="primary" onClick={handleSend} disabled={!input.trim() || !hasDefaultFlow || loading} sx={{ minWidth: 44, minHeight: 44 }}>
            <SendIcon />
          </IconButton>
          {!hasDefaultFlow && appId && !citizenMode && (
            <Button size="small" startIcon={<SettingsIcon />} onClick={() => navigate(settingsPath)} sx={isSFF ? { minHeight: 44 } : {}}>
              {isSFF ? 'Configure' : 'Configure'}
            </Button>
          )}
          {(hasProgress || (hasForm && !isSFF)) && (
            <IconButton size="small" onClick={() => setExpanded((e) => !e)} sx={{ minWidth: 44, minHeight: 44 }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
        {!isSFF && (
          <Collapse in={expanded || hasForm || hasProgress}>
            <Box sx={{ px: 2, pb: 2 }}>
              {(loading || activeFlowState.isActive) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LinearProgress
                    variant={activeFlowState.progress ? 'determinate' : 'indeterminate'}
                    value={activeFlowState.progress || 0}
                    sx={{ flex: 1, height: 4, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {activeFlowState.elapsedSeconds ?? 0}s
                  </Typography>
                  {activeFlowState.currentNode && (
                    <Chip size="small" label={activeFlowState.currentNode} sx={{ fontSize: '0.65rem' }} />
                  )}
                </Box>
              )}
              {hasForm && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  {formContent}
                </Paper>
              )}
            </Box>
          </Collapse>
        )}
        {isSFF && hasProgress && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 1 }}>
            <LinearProgress
              variant={activeFlowState.progress ? 'determinate' : 'indeterminate'}
              value={activeFlowState.progress || 0}
              sx={{ flex: 1, height: 4, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {activeFlowState.elapsedSeconds}s
            </Typography>
            {activeFlowState.currentNode && (
              <Chip size="small" label={activeFlowState.currentNode} sx={{ fontSize: '0.65rem' }} />
            )}
          </Box>
        )}
      </Box>
      {showFormInDialog && (
        <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} fullScreen={isSFF} maxWidth="sm" fullWidth>
          <DialogTitle>{activeFlowState.formTitle || 'Form input'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {(activeFlowState.formFields || []).map((field, idx) => (
                <FormFieldRenderer
                  key={field.name || field.id || idx}
                  type={field.type || 'text'}
                  label={field.label || field.name}
                  value={formData[field.name || field.id] ?? ''}
                  onChange={(e) => handleFormFieldChange(field.name || field.id, e?.target?.value ?? e)}
                  required={field.required}
                  options={field.options}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setFormDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleFormSubmit} disabled={formSubmitting} startIcon={formSubmitting ? <CircularProgress size={16} /> : null} sx={{ minHeight: 44 }}>
              {formSubmitting ? 'Submitting…' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
